import { TanStackDevtools } from "@tanstack/react-devtools"
import { createRootRoute, HeadContent, Scripts } from "@tanstack/react-router"
import { TanStackRouterDevtoolsPanel } from "@tanstack/react-router-devtools"
import { Analytics } from "@vercel/analytics/react"
import { Footer } from "@/components/Footer"
import { Navbar } from "@/components/Navbar"

import appCss from "../styles.css?url"

const SITE_URL = "https://eklo-seven.vercel.app"
const SITE_NAME = "EKLO-Verse"
const DEFAULT_TITLE = "EKLO | Welcome to EKLO-Verse"
const DEFAULT_DESCRIPTION =
  "EKLO. एक्लो. The last age forgets. He remembers. A sci-fi descent into Kalyug's final hour — comics, film, and world."
const OG_IMAGE = `${SITE_URL}/og-cover.png`

const ldJson = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Organization",
      "@id": `${SITE_URL}/#org`,
      name: SITE_NAME,
      url: SITE_URL,
      logo: OG_IMAGE,
    },
    {
      "@type": "WebSite",
      "@id": `${SITE_URL}/#site`,
      url: SITE_URL,
      name: SITE_NAME,
      description: DEFAULT_DESCRIPTION,
      publisher: { "@id": `${SITE_URL}/#org` },
      inLanguage: "en",
    },
    {
      "@type": "CreativeWorkSeries",
      "@id": `${SITE_URL}/#series`,
      name: "EKLO",
      alternateName: "एक्लो",
      description: DEFAULT_DESCRIPTION,
      genre: ["Science Fiction", "Mythology", "Dystopian"],
      url: SITE_URL,
      image: OG_IMAGE,
      publisher: { "@id": `${SITE_URL}/#org` },
    },
  ],
}

export const Route = createRootRoute({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      {
        name: "viewport",
        content: "width=device-width, initial-scale=1",
      },
      { title: DEFAULT_TITLE },
      { name: "description", content: DEFAULT_DESCRIPTION },
      {
        name: "keywords",
        content:
          "EKLO, एक्लो, Eklo-Verse, Kalyug, Kalki, sci-fi, mythology, comics, indie film, dystopian, 2090",
      },
      { name: "author", content: SITE_NAME },
      { name: "theme-color", content: "#03050a" },
      { name: "color-scheme", content: "dark" },
      { name: "robots", content: "index, follow, max-image-preview:large" },
      { name: "format-detection", content: "telephone=no" },

      { property: "og:type", content: "website" },
      { property: "og:site_name", content: SITE_NAME },
      { property: "og:title", content: DEFAULT_TITLE },
      { property: "og:description", content: DEFAULT_DESCRIPTION },
      { property: "og:url", content: SITE_URL },
      { property: "og:image", content: OG_IMAGE },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:alt", content: "EKLO — A reckoning. 2090." },
      { property: "og:locale", content: "en_US" },

      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: DEFAULT_TITLE },
      { name: "twitter:description", content: DEFAULT_DESCRIPTION },
      { name: "twitter:image", content: OG_IMAGE },
      { name: "twitter:image:alt", content: "EKLO — A reckoning. 2090." },
    ],
    links: [
      {
        rel: "icon",
        type: "image/svg+xml",
        href: "/favicon.svg",
      },
      { rel: "manifest", href: "/manifest.json" },
      { rel: "sitemap", type: "application/xml", href: "/sitemap.xml" },
      {
        rel: "stylesheet",
        href: appCss,
      },
    ],
  }),
  notFoundComponent: () => (
    <main className="relative flex min-h-svh flex-col items-center justify-center px-6 text-center text-white">
      <p className="font-mono text-[11px] text-white/45 uppercase tracking-[0.4em]">
        Signal lost · 404
      </p>
      <h1 className="filter-[drop-shadow(0_0_0.18em_rgb(var(--brand-rgb)/0.45))_drop-shadow(0_0_0.45em_rgb(var(--brand-rgb)/0.3))_drop-shadow(0_0_0.9em_rgb(var(--brand-rgb)/0.15))] mt-6 font-bold font-mono text-[clamp(3rem,12vw,8rem)] text-white uppercase leading-[0.9] tracking-[-0.04em] [-webkit-text-stroke:2px_#ffffff] [paint-order:stroke]">
        404
      </h1>
      <p className="mt-6 max-w-[42ch] font-mono text-sm text-white/75 leading-relaxed">
        The transmission ended before it began. The page you sought does not
        exist in this branch of the EKLO-Verse.
      </p>
      <a
        href="/"
        className="mt-10 inline-flex items-center gap-2 border border-white/25 px-5 py-2.5 font-mono text-[11px] text-white uppercase tracking-[0.32em] transition-colors hover:border-brand hover:text-brand"
      >
        Return home
      </a>
    </main>
  ),
  shellComponent: RootDocument,
})

function RootDocument({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
        <script
          type="application/ld+json"
          // biome-ignore lint/security/noDangerouslySetInnerHtml: structured data
          dangerouslySetInnerHTML={{ __html: JSON.stringify(ldJson) }}
        />
      </head>
      <body>
        <Navbar />
        {children}
        <Footer />
        <Analytics />
        {import.meta.env.DEV && (
          <TanStackDevtools
            config={{
              position: "bottom-right",
            }}
            plugins={[
              {
                name: "Tanstack Router",
                render: <TanStackRouterDevtoolsPanel />,
              },
            ]}
          />
        )}
        <Scripts />
      </body>
    </html>
  )
}
